import { SectionedFormBuilder } from '@folio/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { policyControllerListBranchesQueryKey, useBranchControllerCreateBranch } from 'korint-sdk';
import type { FormSections } from 'korint-sdk/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as zod from 'zod';
import { FunnelStepHeader } from '../../components/layout/FunnelStepHeader';
import { useStepperContext } from '../../components/layout/StepperLayout/useStepperContext';
import { StepperFooter } from '../../components/navigation/stepper-footer';
import { useAssertedParams } from '../../lib/utils';
import { Stack } from '@folio/components/ui/flex';

type MtaCreationFunnelModuleProps = {
  productId: string;
  title?: string;
  subtitle?: string;
};

export function MtaCreationFunnelModule({
  productId,
  title,
  subtitle,
}: MtaCreationFunnelModuleProps) {
  const { t } = useTranslation();
  const stepper = useStepperContext();
  const { policyId } = useAssertedParams();
  const form = useForm({
    defaultValues: {
      mtaName: '',
    },
    resolver: zodResolver(
      zod.object({
        mtaName: zod.string().min(1),
      }),
    ),
  });

  const queryClient = useQueryClient();
  const { mutateAsync: createBranch } = useBranchControllerCreateBranch();

  const sections: FormSections = [
    {
      title: t([`funnelSteps.mta.creation.${productId}.title`, 'funnelSteps.mta.creation.title']),
      description: t([
        `funnelSteps.mta.creation.${productId}.description`,
        'funnelSteps.mta.creation.description',
      ]),
      fields: [
        {
          name: 'mtaName',
          type: 'text',
          required: true,
          DEPRECATED_placeholder: t([
            `funnelSteps.mta.creation.${productId}.fieldPlaceholder`,
            'funnelSteps.mta.creation.fieldPlaceholder',
          ]),
        },
      ],
    },
  ];

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={title ?? t('funnelSteps.mta.title')}
        subtitle={subtitle ?? t('funnelSteps.mta.subtitle', '')}
      />
      <SectionedFormBuilder sections={sections} form={form}>
        <StepperFooter
          nextButtonEnabled
          onNext={form.handleSubmit(async values => {
            const branch = await createBranch({
              policyId: policyId,
              data: {
                branchType: 'MTA',
                name: values.mtaName,
              },
            });
            await queryClient.invalidateQueries({
              queryKey: policyControllerListBranchesQueryKey(policyId),
            });
            stepper.next({ policyId: policyId!, branchId: branch.id });
          })}
          loading={form.formState.isSubmitting}
        />
      </SectionedFormBuilder>
    </Stack>
  );
}
